import React, { useState, useEffect } from 'react';
import { IChatConversation } from '../../../interfaces/chat/chat.interface';
import * as S from './styles';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { HiChatAlt2, HiStar, HiSearch, HiX } from 'react-icons/hi';
import { ButtonSimple } from '../../newStudent/Buttons';
import ButtonIcon from '../../newStudent/Buttons/ButtonIcon';
import { useTheme } from 'styled-components';

interface IChatListProps {
  conversations: IChatConversation[];
  onConversationSelect: (conversation: IChatConversation) => void;
  selectedConversationId: number | null;
  onNewChatClick?: () => void;
  isModalOpen?: boolean;
}

export default function ChatList({
  conversations,
  onConversationSelect,
  selectedConversationId,
  onNewChatClick,
  isModalOpen,
}: IChatListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const theme = useTheme();

  useEffect(() => {
    if (isModalOpen === false) {
      setSearchTerm('');
    }
  }, [isModalOpen]);

  const formatTime = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), {
        addSuffix: false,
        locale: ptBR,
      });
    } catch {
      return '';
    }
  };

  const getConversationName = (conversation: IChatConversation) => {
    if (conversation.type === 'group' && conversation.name) {
      return conversation.name;
    }
    if (conversation.otherParticipant?.user?.name) {
      return conversation.otherParticipant.user.name;
    }
    return 'Usuário';
  };

  const getConversationAvatar = (conversation: IChatConversation) => {
    if (conversation.otherParticipant?.user?.avatar) {
      return conversation.otherParticipant.user.avatar;
    }
    return null;
  };

  const filterConversations = (conversationsList: IChatConversation[]) => {
    if (!searchTerm.trim()) {
      return conversationsList;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    return conversationsList.filter((conv) => {
      const conversationName = getConversationName(conv).toLowerCase();
      return conversationName.includes(searchLower);
    });
  };

  const favoriteConversations = filterConversations(conversations.filter((conv) => conv.isFavorited));
  const otherConversations = filterConversations(conversations.filter((conv) => !conv.isFavorited));
  const groupConversations = otherConversations.filter((conv) => conv.type === 'group');
  const directConversations = otherConversations.filter((conv) => conv.type === 'direct');

  return (
    <S.Container>
      <S.Header>
        <S.Title>Conversas</S.Title>
        {onNewChatClick && (
          <ButtonSimple color="primary" font="p" widthSize="xmedium" heightSize="small" startIcon={<HiChatAlt2 size={18} />} onClick={onNewChatClick}>
            Nova conversa
          </ButtonSimple>
        )}
      </S.Header>
      <S.SearchWrapper>
        <S.SearchIcon>
          <HiSearch />
        </S.SearchIcon>
        <S.SearchInput
          type="text"
          placeholder="Buscar conversa..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <S.ClearButtonWrapper>
            <ButtonIcon 
              onClick={() => setSearchTerm('')} 
              color="transparent" 
              icon={<HiX size={16} />} 
            />
          </S.ClearButtonWrapper>
        )}
      </S.SearchWrapper>
      <S.List>
        {conversations.length === 0 ? (
          <S.EmptyState>Nenhuma conversa encontrada</S.EmptyState>
        ) : (
          <>
            {favoriteConversations.length > 0 && (
              <S.Section>
                <S.SectionTitle>
                  <HiStar size={16} color={theme.colors?.warning} />
                  <S.SectionTitleText>Favoritos</S.SectionTitleText>
                </S.SectionTitle>
                {favoriteConversations.map((conversation) => (
                  <S.ConversationItem
                    key={conversation.id}
                    onClick={() => onConversationSelect(conversation)}
                    selected={conversation.id === selectedConversationId}
                  >
                    <S.AvatarContainer>
                      {getConversationAvatar(conversation) ? (
                        <S.Avatar src={getConversationAvatar(conversation) || ''} alt="Avatar" />
                      ) : (
                        <S.AvatarPlaceholder $isGroup={conversation.type === 'group'} $isFavorited={conversation.isFavorited}>
                          {getConversationName(conversation).charAt(0).toUpperCase()}
                        </S.AvatarPlaceholder>
                      )}
                      {conversation.otherParticipant?.user && (
                        conversation.otherParticipant.user.isOnline === true ? (
                          <S.OnlineIndicator />
                        ) : conversation.otherParticipant.user.isOnline === false ? (
                          <S.OfflineIndicator />
                        ) : null
                      )}
                    </S.AvatarContainer>
                    <S.ConversationContent>
                      <S.ConversationHeader>
                        <S.ConversationName>{getConversationName(conversation)}</S.ConversationName>
                        {conversation.timestamp && (
                          <S.Timestamp>{formatTime(conversation.timestamp)}</S.Timestamp>
                        )}
                      </S.ConversationHeader>
                      <S.LastMessage>{conversation.lastMessage || 'Nova conversa'}</S.LastMessage>
                      {conversation.unreadCount && conversation.unreadCount > 0 ? (
                        <S.UnreadBadge>{conversation.unreadCount}</S.UnreadBadge>
                       ) : null}
                    </S.ConversationContent>
                  </S.ConversationItem>
                ))}
              </S.Section>
            )}

            {otherConversations.length > 0 && (
              <S.Section>
                <S.SectionTitle>
                  <S.SectionTitleText>Todas as Conversas</S.SectionTitleText>
                </S.SectionTitle>
                
                {groupConversations.length > 0 && (
                  <S.SubSection>
                    <S.SubSectionTitle>Grupos</S.SubSectionTitle>
                    {groupConversations.map((conversation) => (
                      <S.ConversationItem
                        key={conversation.id}
                        onClick={() => onConversationSelect(conversation)}
                        selected={conversation.id === selectedConversationId}
                      >
                        <S.AvatarContainer>
                          {getConversationAvatar(conversation) ? (
                            <S.Avatar src={getConversationAvatar(conversation) || ''} alt="Avatar" />
                          ) : (
                            <S.AvatarPlaceholder $isGroup={conversation.type === 'group'} $isFavorited={conversation.isFavorited}>
                              {getConversationName(conversation).charAt(0).toUpperCase()}
                            </S.AvatarPlaceholder>
                          )}
                          {conversation.otherParticipant?.user && (
                            conversation.otherParticipant.user.isOnline === true ? (
                              <S.OnlineIndicator />
                            ) : conversation.otherParticipant.user.isOnline === false ? (
                              <S.OfflineIndicator />
                            ) : null
                          )}
                        </S.AvatarContainer>
                        <S.ConversationContent>
                          <S.ConversationHeader>
                            <S.ConversationName>{getConversationName(conversation)}</S.ConversationName>
                            {conversation.timestamp && (
                              <S.Timestamp>{formatTime(conversation.timestamp)}</S.Timestamp>
                            )}
                          </S.ConversationHeader>
                          <S.LastMessage>{conversation.lastMessage || 'Nova conversa'}</S.LastMessage>
                          {conversation.unreadCount && conversation.unreadCount > 0 ? (
                            <S.UnreadBadge>{conversation.unreadCount}</S.UnreadBadge>
                           ) : null}
                        </S.ConversationContent>
                      </S.ConversationItem>
                    ))}
                  </S.SubSection>
                )}

                {directConversations.length > 0 && (
                  <S.SubSection>
                    <S.SubSectionTitle>Conversas Individuais</S.SubSectionTitle>
                    {directConversations.map((conversation) => (
                      <S.ConversationItem
                        key={conversation.id}
                        onClick={() => onConversationSelect(conversation)}
                        selected={conversation.id === selectedConversationId}
                      >
                        <S.AvatarContainer>
                          {getConversationAvatar(conversation) ? (
                            <S.Avatar src={getConversationAvatar(conversation) || ''} alt="Avatar" />
                          ) : (
                            <S.AvatarPlaceholder $isGroup={conversation.type === 'group'} $isFavorited={conversation.isFavorited}>
                              {getConversationName(conversation).charAt(0).toUpperCase()}
                            </S.AvatarPlaceholder>
                          )}
                          {conversation.otherParticipant?.user && (
                            conversation.otherParticipant.user.isOnline === true ? (
                              <S.OnlineIndicator />
                            ) : conversation.otherParticipant.user.isOnline === false ? (
                              <S.OfflineIndicator />
                            ) : null
                          )}
                        </S.AvatarContainer>
                        <S.ConversationContent>
                          <S.ConversationHeader>
                            <S.ConversationName>{getConversationName(conversation)}</S.ConversationName>
                            {conversation.timestamp && (
                              <S.Timestamp>{formatTime(conversation.timestamp)}</S.Timestamp>
                            )}
                          </S.ConversationHeader>
                          <S.LastMessage>{conversation.lastMessage || 'Nova conversa'}</S.LastMessage>
                          {conversation.unreadCount && conversation.unreadCount > 0 ? (
                            <S.UnreadBadge>{conversation.unreadCount}</S.UnreadBadge>
                           ) : null}
                        </S.ConversationContent>
                      </S.ConversationItem>
                    ))}
                  </S.SubSection>
                )}
              </S.Section>
            )}
          </>
        )}
      </S.List>
    </S.Container>
  );
}

